import { useEffect, useState } from 'react';
import { Link } from 'react-router';

import { Alert } from '@ds/feedback';
import { Switch } from '@ds/forms';
import { useAviso } from '@/comuns/toast';
import { dados } from '@/data/porta';

/**
 * O interruptor que diz se a VecchIA pode falar com paciente sozinha.
 *
 * O agendador roda a cada 5 minutos, mas só age em clínica que ligou isto: a
 * régua e as automações podem estar "ativas" desde a semente, e mandar mensagem
 * a um telefone que ninguém conferiu é pior que não mandar. A tela diz as duas
 * faltas separadas — o interruptor e o canal —, porque são decisões diferentes.
 */
export function EnvioAutomatico({ canalConectado }: { canalConectado: boolean }) {
    const aviso = useAviso();
    /* Nulo enquanto não se sabe; a leitura que falha deixa o interruptor
       travado em vez de mostrar "desligado" como se fosse fato. */
    const [ligado, setLigado] = useState<boolean | null>(null);
    const [salvando, setSalvando] = useState(false);
    /* A leitura que falha ia só para o console, e a tela dizia "desligado". */
    const [erroDaLeitura, setErroDaLeitura] = useState<string | null>(null);

    useEffect(() => {
        let vivo = true;
        dados().enviosAutomaticos()
            .then((v) => { if (vivo) { setLigado(v); setErroDaLeitura(null); } })
            .catch((e: unknown) => {
                if (!vivo) return;
                setLigado(null);
                setErroDaLeitura(e instanceof Error ? e.message : 'O servidor não respondeu.');
            });
        return () => { vivo = false; };
    }, []);

    const alternar = async (valor: boolean) => {
        setSalvando(true);
        try {
            setLigado(await dados().definirEnviosAutomaticos(valor));
            aviso({
                title: valor ? 'Envio automático ligado' : 'Envio automático desligado',
                description: valor
                    ? 'Lembretes, automações e fluxos passam a rodar a cada 5 minutos.'
                    : 'Nada roda sozinho até ligar de novo.',
            });
        } catch (e) {
            aviso({ title: 'Não foi possível mudar o envio automático', description: e instanceof Error ? e.message : undefined, tone: 'danger' });
        } finally {
            setSalvando(false);
        }
    };

    const titulo = erroDaLeitura
        ? 'Não foi possível ler o envio automático'
        : !ligado
            ? 'O envio automático está desligado'
            : canalConectado
                ? 'O agendador roda a cada 5 minutos'
                : 'Envio automático ligado, mas sem WhatsApp conectado';

    return (
        <Alert tone={ligado && canalConectado ? 'info' : 'warning'} dismissible={false} title={titulo}>
            <p style={{ margin: 0 }}>
                {erroDaLeitura && `${erroDaLeitura} Enquanto não se sabe, o interruptor fica travado — recarregue a página para tentar de novo.`}
                {!ligado && !erroDaLeitura && 'Enquanto estiver desligado, nada roda sozinho: régua de lembretes, automações e fluxos esperam. As regras que só criam tarefa podem ser executadas à mão, aqui mesmo.'}
                {ligado && canalConectado && 'Lembretes da régua, automações ativas e fluxos ativos saem pelo WhatsApp conectado, cada um uma vez só. A confirmação e o lembrete de 24 h que a régua já manda não saem de novo pela automação.'}
                {ligado && !canalConectado && 'As tarefas rodam; mensagem não tem por onde sair.'}
                {!canalConectado && (
                    <>
                        {' '}Conecte o WhatsApp em{' '}
                        <Link to="/app/configuracoes/integracoes" style={{ color: 'var(--text-brand)', fontWeight: 600 }}>
                            Configurações › Integrações
                        </Link>
                        .
                    </>
                )}
            </p>
            <div style={{ marginTop: 10 }}>
                <Switch
                    checked={Boolean(ligado)}
                    disabled={ligado === null || salvando}
                    onChange={(v) => { void alternar(v); }}
                    label="Enviar automaticamente pelo WhatsApp"
                    description="Só a proprietária, ou quem tem a permissão de configurações, muda isto."
                />
            </div>
        </Alert>
    );
}
